import React, { useState, useEffect, useRef } from 'react';
import logo from '../../Assets/Logo/logo.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../../config/firebase";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import DefaultSEO from '../../components/DefaultSEO';

// Remove unused top-level function

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const isLoggingIn = useRef(false);

  // Redirect logged-in users away from LoginPage
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

    // Firebase Recaptcha removed as we use backend OTP

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (phone.length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }

    setLoading(true);

    try {
      const formatPhone = '+' + phone;
      const cleanApiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');

      const response = await fetch(`${cleanApiBase}/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ phone: formatPhone })
      });

      const data = await response.json();

      if (data.success) {
        // Navigate to OTP page and pass the phone number and from redirection state
        navigate('/otp', { state: { phone: formatPhone, from: location.state?.from } });
      } else {
        if (data.is_blocked) {
          setIsBlocked(true);
          return;
        }
        throw new Error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error(error);
      setError(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    try {
      isLoggingIn.current = true;
      const result = await signInWithPopup(auth, googleProvider);
      console.log("User Info:", result.user);

      // Get the Firebase ID Token
      const idToken = await result.user.getIdToken();

      try {
        // Send to backend API
        const cleanApiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
        const response = await fetch(`${cleanApiBase}/auth/google-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ firebase_id_token: idToken })
        });

        const data = await response.json();

        if (data.success && data.data?.token) {
          localStorage.setItem('auth_token', data.data.token);
          const from = location.state?.from;
          if (from) {
            navigate(from.pathname || from, { replace: true, state: from.state });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          if (data.is_blocked) {
            setIsBlocked(true);
            isLoggingIn.current = false;
            if (auth) await auth.signOut();
            return;
          }
          throw new Error(data.message || "Backend login failed");
        }
      } catch (backendError) {
        throw backendError; // Forward to the outer catch
      }

    } catch (error) {
      console.error(error);
      setError(error.message || 'Google Login failed.');
      isLoggingIn.current = false;
      await auth.signOut(); // Sign out from Firebase if backend fails
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100">
      <DefaultSEO title="Login | ZoyoTrip" description="Sign in to your ZoyoTrip account to manage bookings and discover exclusive travel deals." />
      <button
        className="btn position-absolute top-0 start-0 m-3 d-lg-none"
        onClick={() => navigate(-1)}
        aria-label="Close"
      >
        <i className="bi bi-x-lg fs-4 fw-bold text-dark"></i>
      </button>

      <div className="login-container p-4 w-100" style={{ maxWidth: '420px' }}>
        {/* Logo */}
        <div className="d-flex flex-column align-items-center mb-4">
          <Link to={'/'}>
            <img src={logo} alt="ZoyoTrip" width={120} />
          </Link>
          <h5 className="mt-2 text-primary fw-bold">Welcome Back!</h5>
          <p className="text-muted small">{isBlocked ? 'Account access is currently restricted' : 'Login with your phone number to continue'}</p>
        </div>

        {isBlocked ? (
            <div className="text-center py-4">
                <div className="mb-4">
                    <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '3rem' }}></i>
                </div>
                <h4 className="fw-bold text-dark mb-3">Account Suspended</h4>
                <p className="text-muted mb-4">
                    Oops! Your account has been suspended. Please reach out to our support team for assistance.
                </p>
                <div className="d-grid gap-3">
                    <a href="mailto:contact@zoyotrip.com" className="btn btn-primary rounded-pill py-2">
                        <i className="bi bi-envelope me-2"></i> Email contact@zoyotrip.com
                    </a>
                    <a href="tel:+919826178619" className="btn btn-outline-primary rounded-pill py-2">
                        <i className="bi bi-telephone me-2"></i> Call +91 9826178619
                    </a>
                </div>
                <button type="button" className="btn btn-link text-muted mt-3 text-decoration-none" onClick={() => setIsBlocked(false)}>
                    Back to Login
                </button>
            </div>
        ) : (
            <>

        {error && <div className="alert alert-danger small py-2">{error}</div>}

        {/* Login Form */}
        <form onSubmit={handlePhoneSubmit}>
          <div className="mb-4">
            <label className="form-label">Mobile Number</label>
            <PhoneInput
              country={'in'}
              value={phone}
              onChange={phone => setPhone(phone)}
              inputClass="form-control w-100"
              containerClass="w-100"
              inputStyle={{ height: '45px', width: '100%' }}
            />
          </div>


          {/* Submit Button */}
          <div className="d-grid gap-2 mb-3">
            <button type="submit" className="btn btn-primary rounded-pill py-2" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Continue with Phone'}
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="text-center my-3 text-muted">
          <small>OR</small>
        </div>

        {/* Google Login */}
        <div className="d-grid gap-2">
          <button type="button" onClick={googleLogin} className="btn btn-outline-danger rounded-pill py-2 d-flex align-items-center justify-content-center gap-2">
            <i className="bi bi-google"></i> Continue with Google
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <small className="text-muted">
            By continuing, you agree to our{' '}
            <Link to="/terms-and-conditions" className="text-primary text-decoration-none">Terms</Link> and{' '}
            <Link to="/privacy-policy" className="text-primary text-decoration-none">Privacy Policy</Link>.
          </small>
        </div>
      </>
      )}
      </div>
    </div>
  );
};

export default LoginPage;
